export const CONNECTION_PROVIDER_IDS = [
    "apple-health",
    "google",
    "google-health",
    "fitbit",
    "oura",
    "garmin",
    "whoop",
    "polar",
    "peloton",
    "ultrahuman",
    "strava",
    "myfitnesspal",
    "flow",
    "withings",
    "dexcom",
    "calendar-google",
    "calendar-microsoft",
    "ehr-epic",
    "ehr-cerner",
    "terra",
    "health-connect",
];
export const CONNECTION_PROVIDER_LABELS = {
    "apple-health": "Apple Health",
    google: "Google",
    "google-health": "Google Health",
    fitbit: "Fitbit",
    oura: "Oura",
    garmin: "Garmin",
    whoop: "WHOOP",
    polar: "Polar",
    peloton: "Peloton",
    ultrahuman: "Ultrahuman",
    strava: "Strava",
    myfitnesspal: "MyFitnessPal",
    flow: "Flow",
    withings: "Withings",
    dexcom: "Dexcom",
    "calendar-google": "Google Calendar",
    "calendar-microsoft": "Outlook Calendar",
    "ehr-epic": "Epic MyChart",
    "ehr-cerner": "Oracle Cerner",
    terra: "Wearables",
    "health-connect": "Health Connect",
};
export const TERRA_RESOURCES = [
    "GOOGLE",
    // Terra renamed its fitbit_v2 provider to google_health in the July 2026
    // update and stopped accepting the old name. It is the Google Health cloud
    // API (health.googleapis.com), reading every source attached to the member's
    // Google account, so it is the successor to the Fitbit Web API sunsetting in
    // September 2026 rather than a new source alongside it.
    "GOOGLE_HEALTH",
    "FITBIT",
    "OURA",
    "GARMIN",
    "WHOOP",
    "POLAR",
    "PELOTON",
    "ULTRAHUMAN",
    "STRAVA",
    "MYFITNESSPAL",
    "FLO",
    "WITHINGS",
    // SDK-only device health sources: reached through the Terra mobile SDK,
    // never through the widget. Listed so terraConnectionProvider resolves each
    // to its own connection provider instead of falling back to the generic
    // "terra" bucket, which is also where any genuinely unrecognised Terra
    // resource lands. Without its own entry, a device health source and an
    // unmapped Terra connection would be indistinguishable in user_connections.
    "APPLE_HEALTH",
    "HEALTH_CONNECT",
];
export const TERRA_RESOURCE_BY_CONNECTION_PROVIDER = {
    google: "GOOGLE",
    "google-health": "GOOGLE_HEALTH",
    fitbit: "FITBIT",
    oura: "OURA",
    garmin: "GARMIN",
    whoop: "WHOOP",
    polar: "POLAR",
    peloton: "PELOTON",
    ultrahuman: "ULTRAHUMAN",
    strava: "STRAVA",
    myfitnesspal: "MYFITNESSPAL",
    flow: "FLO",
    withings: "WITHINGS",
    "apple-health": "APPLE_HEALTH",
    "health-connect": "HEALTH_CONNECT",
};
export const TERRA_BACKED_CONNECTION_PROVIDERS = Object.keys(TERRA_RESOURCE_BY_CONNECTION_PROVIDER);
export const TERRA_CONNECTION_PROVIDERS = ["terra", ...TERRA_BACKED_CONNECTION_PROVIDERS];
export const TERRA_WEARABLE_CONNECTION_PROVIDERS = [
    "oura",
    "garmin",
    "whoop",
    "polar",
    "ultrahuman",
    "fitbit",
    // Google Health carries the member's Fitbit history plus whatever else is
    // attached to their Google account, so it counts wherever Fitbit counts.
    "google-health",
];
/** Every provider that counts as a wearable, for SQL IN-lists and joins. */
export const WEARABLE_CONNECTION_PROVIDERS = [
    "terra",
    "apple-health",
    "apple_health",
    "health-connect",
    "health_connect",
    ...TERRA_WEARABLE_CONNECTION_PROVIDERS,
];
const WEARABLE_CONNECTION_PROVIDER_SET = new Set(WEARABLE_CONNECTION_PROVIDERS);
const TERRA_CONNECTION_PROVIDER_BY_RESOURCE = {
    GOOGLE: "google",
    GOOGLE_HEALTH: "google-health",
    FITBIT: "fitbit",
    OURA: "oura",
    GARMIN: "garmin",
    WHOOP: "whoop",
    POLAR: "polar",
    PELOTON: "peloton",
    ULTRAHUMAN: "ultrahuman",
    STRAVA: "strava",
    MYFITNESSPAL: "myfitnesspal",
    FLO: "flow",
    WITHINGS: "withings",
    APPLE_HEALTH: "apple-health",
    HEALTH_CONNECT: "health-connect",
};
export function terraConnectionProvider(resource) {
    if (!resource)
        return "terra";
    return TERRA_CONNECTION_PROVIDER_BY_RESOURCE[resource.toUpperCase()] ?? "terra";
}
export function isTerraConnectionProvider(provider) {
    return TERRA_CONNECTION_PROVIDERS.includes(provider);
}
export function isWearableConnectionProvider(provider) {
    return WEARABLE_CONNECTION_PROVIDER_SET.has(provider);
}
export function terraResourceForConnectionProvider(provider) {
    if (!provider || !(provider in TERRA_RESOURCE_BY_CONNECTION_PROVIDER))
        return null;
    return TERRA_RESOURCE_BY_CONNECTION_PROVIDER[provider];
}
/**
 * Connection providers that are device health sources: read on the member's
 * own phone through the Terra mobile SDK (Apple Health via HealthKit, Health
 * Connect on Android), rather than fetched server-to-server from a vendor
 * cloud like every other Terra-backed connection. The single place that knows
 * which stored provider strings identify each one, so a connection's "is this
 * Apple Health" question is answered the same way everywhere instead of by
 * separate hand-rolled matchers per surface - previously two of those
 * matchers also accepted the generic "terra" fallback provider as Apple
 * Health, so any unmapped Terra connection could masquerade as a working
 * Apple Health link.
 */
export const DEVICE_HEALTH_CONNECTION_PROVIDERS = ["apple-health", "health-connect"];
/**
 * Every stored provider string that should be read as this device health
 * source. Includes the snake_case spelling some payloads and hand-written
 * fixtures still use alongside the kebab-case connection provider id; never
 * includes "terra", which is the fallback for a genuinely unmapped resource.
 */
const DEVICE_HEALTH_PROVIDER_ALIASES = {
    "apple-health": ["apple-health", "apple_health"],
    "health-connect": ["health-connect", "health_connect"],
};
/** Whether a stored `user_connections.provider` value is this device health source. */
export function isDeviceHealthConnection(provider, source) {
    return DEVICE_HEALTH_PROVIDER_ALIASES[source].includes(provider.trim().toLowerCase());
}
/** Find the connection row for a device health source, or null if unlinked. */
export function findDeviceHealthConnection(connections, source) {
    return (connections.find((connection) => isDeviceHealthConnection(connection.provider, source)) ?? null);
}
/** DB/API provider keys that map to UI connection provider ids. */
export const API_PROVIDER_ALIASES = {
    google_calendar: "calendar-google",
    outlook_calendar: "calendar-microsoft",
    apple_health: "apple-health",
    health_connect: "health-connect",
    google_health: "google-health",
};
/** Display overrides for API keys that differ from CONNECTION_PROVIDER_LABELS. */
const USER_CONNECTION_PROVIDER_OVERRIDES = {
    oura: "Oura Ring",
    google: "Google Fit",
    apple_watch: "Apple Watch",
    terra: "Wearable",
    healthos_mobile: "HealthOS Mobile",
};
function formatSnakeCaseProviderLabel(provider) {
    return provider
        .split("_")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}
/** Human label for a provider string from user_connections (API/DB shape). */
export function userConnectionProviderLabel(provider) {
    const normalized = provider.trim().toLowerCase();
    const override = USER_CONNECTION_PROVIDER_OVERRIDES[normalized];
    if (override)
        return override;
    const aliased = API_PROVIDER_ALIASES[normalized];
    if (aliased)
        return CONNECTION_PROVIDER_LABELS[aliased];
    if (CONNECTION_PROVIDER_IDS.includes(normalized)) {
        return CONNECTION_PROVIDER_LABELS[normalized];
    }
    return formatSnakeCaseProviderLabel(normalized);
}
/**
 * The readings each provider can send us, as the Terra and Apple Health
 * mappers write them. A declared kind with no signal behind it is stated as
 * pending rather than dropped: "not yet" is a different fact from "never".
 */
export const CONNECTION_PROVIDER_KINDS = {
    oura: [
        "wearable.sleep",
        "wearable.hrv",
        "wearable.rhr",
        "wearable.activity",
        // Oura logs walks and rides as Terra activity events; leaving this out
        // told a member their ring never sends training it sends daily.
        "wearable.workout",
        "wearable.skin_temp",
        "wearable.respiration",
        "wearable.spo2",
    ],
    whoop: [
        "wearable.sleep",
        "wearable.hrv",
        "wearable.rhr",
        "wearable.activity",
        "wearable.workout",
    ],
    garmin: [
        "wearable.sleep",
        "wearable.activity",
        "wearable.rhr",
        "wearable.stress",
        "wearable.workout",
    ],
    fitbit: ["wearable.sleep", "wearable.activity", "wearable.rhr", "wearable.hrv"],
    "google-health": ["wearable.sleep", "wearable.activity", "wearable.rhr"],
    ultrahuman: ["wearable.sleep", "wearable.hrv", "wearable.activity", "wearable.skin_temp"],
    polar: ["wearable.workout", "wearable.activity", "wearable.rhr"],
    peloton: ["wearable.workout"],
    strava: ["wearable.workout", "wearable.activity"],
    withings: ["wearable.weight", "wearable.bp.systolic", "wearable.bp.diastolic"],
    dexcom: ["wearable.glucose"],
    myfitnesspal: ["nutrition.meal", "nutrition.sodium_mg", "hydration.ml"],
    "apple-health": [
        "wearable.activity",
        "wearable.sleep",
        "wearable.rhr",
        "wearable.workout",
        "wearable.vo2max",
    ],
    "health-connect": ["wearable.activity", "wearable.sleep", "wearable.rhr", "wearable.workout"],
    "calendar-google": ["calendar.event"],
    "calendar-microsoft": ["calendar.event"],
};
/** Providers that reach us through another service, named for the member. */
export const CONNECTION_PROVIDER_VIA = {
    oura: "Terra",
    whoop: "Terra",
    garmin: "Terra",
    fitbit: "Terra",
    "google-health": "Terra",
    ultrahuman: "Terra",
    polar: "Terra",
    peloton: "Terra",
    strava: "Terra",
    withings: "Terra",
    dexcom: "Terra",
    myfitnesspal: "Terra",
};
/**
 * Which device wins when two report the same reading. A dedicated sensor beats
 * an aggregator: the ring measures through the night, the watch counts steps
 * on the wrist, and Apple Health or Health Connect only relay what some other
 * device recorded. Both readings are kept; this decides which one is shown.
 */
export const CONNECTION_PROVIDER_PRECEDENCE = [
    "oura",
    "whoop",
    "garmin",
    "fitbit",
    "ultrahuman",
    "polar",
    "withings",
    "dexcom",
    "peloton",
    "strava",
    "myfitnesspal",
    "apple-health",
    "google-health",
    "google",
    "health-connect",
    "terra",
    "calendar-google",
    "calendar-microsoft",
    "ehr-epic",
    "ehr-cerner",
];
/**
 * The providers that can disagree: two of them can report the same reading on
 * the same day. A calendar or a record system reports something no device
 * else does, so it never enters the comparison.
 */
export const CONNECTION_PROVIDERS_THAT_MEASURE = CONNECTION_PROVIDER_PRECEDENCE.filter((id) => !id.startsWith("calendar-") && !id.startsWith("ehr-") && id !== "terra");
/*
 * Precedence is per reading, not per device: the wrist counts steps better
 * than a ring, and the ring measures the night better than a wrist. A kind
 * with no entry here follows the general order above.
 */
const KIND_PRECEDENCE = {
    "wearable.activity": ["garmin", "fitbit", "apple-health", "health-connect", "google-health"],
    "wearable.workout": ["garmin", "polar", "strava", "peloton", "apple-health"],
    "wearable.vo2max": ["garmin", "apple-health", "whoop"],
};
/** Why one device is read before another for a given reading. */
export const PRECEDENCE_REASONS = {
    "wearable.activity": "A wrist counts steps more accurately than a ring.",
    "wearable.workout": "The device that recorded the session holds the detail.",
    "wearable.sleep": "The ring measures through the night; a watch samples by day.",
    "wearable.hrv": "The ring measures through the night; a watch samples by day.",
    "wearable.rhr": "The ring measures through the night; a watch samples by day.",
};
/** Where a provider sits in that order; an unranked writer sorts last. */
export function providerRank(provider, kind) {
    if (kind) {
        const order = KIND_PRECEDENCE[kind];
        if (order) {
            const id = normalizedProvider(provider);
            const rank = id ? order.indexOf(id) : -1;
            if (rank !== -1)
                return rank;
            // Anything the reading has no opinion about falls in behind those that do.
            return order.length + generalRank(provider);
        }
    }
    return generalRank(provider);
}
/** Seeded readings carry this so a demo number can never pass as a measured one. */
export const DEMO_SOURCE_PREFIX = "demo:";
/** A provider id in its canonical spelling, whatever the writer called it. */
export function normalizedProvider(provider) {
    if (!provider)
        return null;
    const normalized = provider
        .toLowerCase()
        .replace(/^demo[:/]/, "")
        .replace(/^terra[:/]/, "")
        .replace(/_/g, "-");
    return API_PROVIDER_ALIASES[provider.toLowerCase()] ?? normalized;
}
function generalRank(provider) {
    const id = normalizedProvider(provider);
    if (!id)
        return Number.MAX_SAFE_INTEGER;
    const rank = CONNECTION_PROVIDER_PRECEDENCE.indexOf(id);
    return rank === -1 ? Number.MAX_SAFE_INTEGER : rank;
}
/** True when the first source outranks the second for the same reading. */
export function outranks(a, b, kind) {
    return providerRank(a, kind) < providerRank(b, kind);
}
//# sourceMappingURL=connection-providers.js.map